import { useEffect, useState } from 'react';
import { toast } from '../../../util/toasts';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { Typography } from '../../../components/typography/typography';
import { displayMoneyFormat } from '../../../util/util';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Field, FieldLabel } from '../../../components/field/field';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { TextArea } from '../../../components/textarea/textarea';
import { QuantityInput } from '../../../customer/product-form/quantity-input/quantity-input';
import './add-line-item-modal.scss';
import { Radio } from '../../../components/radio/radio';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemGeneralLedgerAccount,
  ItemType,
} from '../../../clients/quipli-service/accounting/accounting';
import { useAccountingApiV1 } from '../../../clients/quipli-service/accounting/accounting-api-v1';
import { TaxCategory } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { LineItemTax } from '../../collectibles/components/edit-line-item-modal';

export const AddLineItemModal = ({
  invoice,
  isOpen,
  close,
  currency,
}: {
  invoice: Invoice;
  isOpen: boolean;
  close: (payments?: any[]) => void;
  currency: string;
}) => {
  const { getItemGeneralLedgerAccount, listAccounts } = useAccountingApiV1();
  const { createLineItem } = useInvoiceApiV1();
  const { searchTaxCategories } = useTaxApiV1();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [isRefund, setIsRefund] = useState<boolean>();
  const [taxApplicable, setTaxApplicable] = useState<boolean>(true);
  const [lineItemName, setLineItemName] = useState<string>('');
  const [comments, setComments] = useState<string>();
  const [lineItemQuantity, setLineItemQuantity] = useState<string>('1');
  const [lineItemQuantityNumber, setLineItemQuantityNumber] = useState<number>(1);
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState<GeneralLedgerAccount>();
  const [defaultUpchargeItemGeneralLedgerAccount, setDefaultUpchargeItemGeneralLedgerAccount] =
    useState<ItemGeneralLedgerAccount>();
  const [defaultRefundItemGeneralLedgerAccount, setDefaultRefundItemGeneralLedgerAccount] =
    useState<ItemGeneralLedgerAccount>();
  const [taxCategory, setTaxCategory] = useState<TaxCategory | undefined>();
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>();

  useEffect(() => {
    setLineItemQuantityNumber(parseFloat(lineItemQuantity));
  }, [lineItemQuantity]);

  useEffect(() => {
    Promise.all([
      listAccounts(false, GeneralLedgerAccountType.Income).then(setAccounts),
      getItemGeneralLedgerAccount(ItemType.Upcharge).then(setDefaultUpchargeItemGeneralLedgerAccount),
      getItemGeneralLedgerAccount(ItemType.Refund).then(setDefaultRefundItemGeneralLedgerAccount),
      invoice.taxCode && searchTaxCategories({}).then(setTaxCategories),
    ]).then(() => setIsRefund(false));
  }, []);

  useEffect(() => {
    if (isRefund && accounts && defaultRefundItemGeneralLedgerAccount) {
      setGeneralLedgerAccount(
        accounts.find((a) => a.id === defaultRefundItemGeneralLedgerAccount.generalLedgerAccountId),
      );
    } else if (!isRefund && accounts && defaultUpchargeItemGeneralLedgerAccount) {
      setGeneralLedgerAccount(
        accounts.find((a) => a.id === defaultUpchargeItemGeneralLedgerAccount.generalLedgerAccountId),
      );
    }
  }, [isRefund]);

  function onGeneralLedgerAccountChange(selected: { label: string; value: string }) {
    setGeneralLedgerAccount(accounts?.find((a) => a.id === selected.value));
  }

  function onTaxCategoryChange(selected?: { label: string; value: string }) {
    if (selected) {
      setTaxCategory(taxCategories?.find((a) => a.id === selected.value));
    } else {
      setTaxCategory(undefined);
    }
  }

  const maxRefund = taxApplicable ? -invoice.subtotal : -invoice.total;

  const taxOverride = isRefund && amount * invoice.taxRate > invoice.tax ? invoice.tax : undefined;
  async function addLineItem() {
    setIsLoading(true);
    if (amount > 0 || (amount < 0 && maxRefund <= amount)) {
      try {
        const lineItem = await createLineItem(invoice.id, {
          name: lineItemName,
          description: comments,
          unitPrice: isRefund ? -1 * amount : amount,
          quantity: lineItemQuantityNumber,
          taxable: taxApplicable,
          taxOverride: isRefund && taxOverride ? -1 * taxOverride : taxOverride,
          generalLedgerAccountId: generalLedgerAccount?.id,
          taxCategoryId: taxCategory?.id,
        });
        toast('Line item successfully added');
        close([lineItem]);
      } catch (ex: any) {
        toast(ex.message.msg || ex.message, 'error');
      } finally {
        setIsLoading(false);
      }
    }
  }

  return (
    <FiltersModal
      isOpen={isOpen}
      title={`Add Line Item to Invoice #${invoice.invoiceNumber}`}
      confirmText="Add Line Item"
      cancelText="Cancel"
      allowConfirm={!isLoading && !!lineItemName && (amount < 0 ? maxRefund <= amount : amount > 0)}
      allowCancel={!isLoading}
      showConfirmButtonFirst={false}
      onConfirm={addLineItem}
      onCancel={() => close(undefined)}
    >
      <div className="min-w-full w-[370px] flex flex-col gap-2 items-stretch">
        <div className="flex gap-5 my-2">
          <div className="flex gap-1 items-center">
            <Radio isChecked={!isRefund} onChange={() => setIsRefund(false)} />
            <FieldLabel label="Upcharge" />
          </div>
          {invoice.documentType === 'Invoice' && (
            <div className="flex gap-1 items-center">
              <Radio isChecked={isRefund} onChange={() => setIsRefund(true)} />
              <FieldLabel label="Refund" />
            </div>
          )}
        </div>

        <FieldLabel label="Line Item Name" />
        <Field type="text" value={lineItemName} onChange={(n) => setLineItemName(n)} />

        <FieldLabel label="General Ledger Account" />
        <Field
          type="dropdown"
          value={{
            label: generalLedgerAccount?.name,
            value: generalLedgerAccount?.id,
          }}
          onChange={(a) => onGeneralLedgerAccountChange(a)}
          options={accounts?.map((a) => ({ label: a.name, value: a.id }))}
        />

        {invoice.taxCode && taxCategories && taxCategories.length > 0 && (
          <>
            <FieldLabel label="Tax Category" />
            <Field
              type="dropdown"
              value={{
                label: taxCategory?.name,
                value: taxCategory?.id,
              }}
              onChange={(a) => onTaxCategoryChange(a)}
              options={taxCategories?.map((a) => ({ label: a.name, value: a.id }))}
              isClearable={true}
            />
          </>
        )}

        <div className="flex justify-between">
          <Field
            label="Amount"
            invalid={amount < 0 && maxRefund > amount * lineItemQuantityNumber}
            type="currency"
            value={amount}
            onChange={(a) => {
              if (Number.isNaN(a) && !amount) {
                setAmount(NaN);
                setAmount(NaN);
              } else setAmount(a);
            }}
            maximumFractionDigits={2}
          />

          <div>
            <FieldLabel label="Quantity" />
            <QuantityInput name="Quantity" value={lineItemQuantity} onChange={(q) => setLineItemQuantity(q ?? '1')} />
          </div>
        </div>
        <div className="mt-2 text-right text-[var(--grey-500)] text-sm">
          Subtotal: {displayMoneyFormat(amount * lineItemQuantityNumber, currency)}
        </div>

        {/* don't allow crediting the invoice for more than the invoice total */}
        {amount * lineItemQuantityNumber < 0 && maxRefund > amount * lineItemQuantityNumber && (
          <Typography type="text-sm" weight="regular" className="text-[var(--error-500)]">
            Refund amount cannot exceed {displayMoneyFormat(maxRefund, currency)}
          </Typography>
        )}

        <div className="flex justify-between mb-3">
          <Checkbox
            isChecked={taxApplicable}
            onChange={(checked) => setTaxApplicable(checked)}
            label="Tax applicable"
          />
          <LineItemTax
            lineItem={{
              name: lineItemName,
              description: comments,
              unitPrice: isRefund ? -1 * amount : amount,
              quantity: lineItemQuantityNumber,
              taxable: taxApplicable,
              generalLedgerAccountId: generalLedgerAccount?.id,
              taxCategoryId: taxCategory?.id,
            }}
            invoice={invoice}
            currency={currency}
          />
        </div>

        <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
          Comments (optional)
        </Typography>
        <TextArea value={comments} onChange={(c) => setComments(c.target.value)} />
      </div>
    </FiltersModal>
  );
};
